/**
 * ẢNH AI CHO SÂN ĐẤU — nền trời, sàn đá, Nhà (3 trạng thái), decor (9 props).
 *
 * VÌ SAO tách riêng file này khỏi `arena.ts`:
 *   `arena.ts` lo BỐ CỤC (chỗ nào, depth nào, che hay không che), còn file này lo
 *   ĐƯỜNG ỐNG ART (giải mã ảnh, tách nền magenta, cắt lưới, cắt sát viền).
 *   Trộn hai việc vào nhau thì lúc ảnh AI lỗi sẽ không biết lỗi ở đâu.
 *
 * HỢP ĐỒNG QUAN TRỌNG — đọc trước khi sửa:
 *   1. `loadBattlefieldArt()` KHÔNG BAO GIỜ ném lỗi và KHÔNG BAO GIỜ chặn game.
 *      Ảnh thiếu (404) ⇒ các trường trả về rỗng ⇒ `ArenaView` giữ nguyên bản vẽ code.
 *   2. Hàm chỉ ĐỌC ảnh và NẠP texture; nó không tạo GameObject nào. Nhờ vậy gọi nó
 *      kiểu `void loadBattlefieldArt(scene).then(...)` trong constructor là an toàn.
 *
 * BẪY ĐÃ GẶP (đừng lặp lại):
 *   · Ảnh gốc nằm ở `art/ai/`, trình duyệt chỉ thấy file trong `public/`.
 *     Quên `npm run sync:art` ⇒ mọi thứ 404 ⇒ sân vẫn là bản vẽ code (trông "ổn"
 *     nên rất dễ tưởng là code sai). Xem `tools/sync-art.mjs`.
 *     BẪY PHỤ, rất dễ mất thời gian: `npm run dev` phục vụ được CẢ file trong thư mục
 *     gốc dự án, nên lúc dev thì ảnh trong `art/ai/` vẫn hiện dù CHƯA sync. Chỉ bản
 *     `build`/`preview` mới lộ ra là thiếu file. Vì vậy: kiểm tra sân thật bằng
 *     `npm run preview` (cổng 4173), đừng tin mỗi dev server.
 *   · Phải tách nền MAGENTA **trước** khi cắt sát viền. Nếu trim trước, hộp bao
 *     luôn bằng đúng cả ô vuông ⇒ Nhà/props bị kéo giãn, lệch khỏi bệ đá.
 *   · Ô lưới chỉ ~341px nhưng prop lớn có thể TRÀN sang ô bên. Không lọc "khối liền
 *     lớn nhất" trước khi trim thì hộp bao dính luôn prop hàng xóm ⇒ prop bị kéo dài.
 *   · KHÔNG dùng `scene.load` (Loader) để nạp: Loader của Phaser báo 404 bằng cách
 *     chặn cả file pack và log khó đọc. Dùng `Image()` + `textures.addCanvas` như
 *     `src/art/ai-parts.ts` — 404 chỉ là `onerror`, game chạy tiếp bình thường.
 *   · Ảnh có thể tới SAU khi constructor chạy xong (agy sinh song song). Vì vậy mọi
 *     thứ "có thể thay bằng ảnh" phải tạo theo kiểu thay thế được (`setTexture`),
 *     không được giả định ảnh đã có ngay lúc dựng sân.
 */
import Phaser from 'phaser';
import { artUrl } from '../art/art-url';

/**
 * CẢNH TỐI THIỂU MÀ VIỆC NẠP ẢNH CẦN — **CHỈ `textures`**, không gì khác.
 *
 * ⚠️ VÌ SAO KHÔNG NHẬN THẲNG `Phaser.Scene` (cùng hướng với `UnitViewsWorld` ở `render/unitview.ts`):
 * hai nơi gọi hàm này có hai kiểu scene KHÁC NHAU — `ArenaView` của game 10-wave cầm `Phaser.Scene`
 * thật, còn `ArenaBattleView` của Arena chỉ khai interface hẹp `BattleScene` (để tầng vẽ dùng được
 * mà không kéo cả Phaser vào). Nhận `Phaser.Scene` thì Arena buộc phải `as unknown as`, tức
 * TypeScript NGỪNG kiểm ở đúng chỗ dễ sai.
 *
 * Khai đúng thứ thật sự dùng (`addPlain`/ `sliceSheet` chỉ gọi `scene.textures`) ⇒ cả hai nơi khớp
 * TỰ NHIÊN, không ép kiểu, và nếu ai đó lỡ dùng thêm thứ khác của scene thì `tsc` báo ngay.
 */
export interface BattlefieldArtScene {
  textures: Phaser.Textures.TextureManager;
}

export interface BattlefieldArt {
  /** khoá texture sàn đá (TILE được) — undefined nếu chưa có ảnh */
  floor?: string;
  /**
   * khoá texture SÂN CÓ SÔNG + 2 CẦU (`arena-river.png`, tỉ lệ 5:3 = ĐÚNG 1000×600).
   *
   * ⚠️⚠️ VÌ SAO PHẢI LÀ TRƯỜNG RIÊNG, KHÔNG ĐƯỢC GỘP VÀO `floor`:
   * `floor` là ảnh gạch LIỀN MẠCH, vẽ kiểu lặp (tile) nên chế độ 10-wave phủ kín sân bằng nó.
   * `river` là MỘT BỨC BẢN ĐỒ: sông + 2 cầu chỉ được nằm ở ĐÚNG một chỗ; lặp nó lên là lỗi thị giác
   * (sông xuất hiện nhiều lần). Hai ảnh có hai luật vẽ KHÁC NHAU nên phải là hai trường — gộp lại thì
   * một trong hai chế độ sẽ vẽ sai, và cái sai đó chỉ lộ ra khi nhìn ảnh chụp.
   * Xem `ArenaBattleView.applyArt` (vẽ MỘT lần) và `ArenaView.applyArt` (vẫn vẽ kiểu phủ kín).
   */
  river?: string;
  /** khoá texture nền trời/làng quê — undefined nếu chưa có ảnh */
  bg?: string;
  /** 3 trạng thái Nhà: [0] nguyên vẹn · [1] hư nhẹ · [2] hư nặng (null = ô trống) */
  house: Array<{ key: string } | null>;
  /** 9 props decor theo đúng thứ tự ô trong lưới (null = ô trống) */
  decor: Array<string | null>;
}

const PATH = {
  floor: 'art/ai/arena-floor.png',
  river: 'art/ai/arena-river.png',
  bg: 'art/ai/arena-bg.png',
  house: 'art/ai/house.png',
  decor: 'art/ai/arena-decor.png',
} as const;

/*
 * Khoá texture có tiền tố `__tdk_arena_` để không đụng các hệ khác
 * (`__tdk_sky`, `__tdk_glow`, các khoá `ai_*` của nhân vật, `vfx_*` của lớp hiệu ứng).
 * Phaser dùng CHUNG một TextureManager cho cả game ⇒ trùng khoá là ghi đè ảnh của nhau.
 */
const KEY = {
  floor: '__tdk_arena_floor',
  river: '__tdk_arena_river',
  bg: '__tdk_arena_bg',
  house: (i: number): string => `__tdk_arena_house_${i}`,
  decor: (i: number): string => `__tdk_arena_decor_${i}`,
} as const;

/** Kết quả rỗng — dùng khi thiếu ảnh. TUYỆT ĐỐI không trả về `null` để nơi gọi khỏi null-check. */
function emptyArt(): BattlefieldArt {
  return { house: [null, null, null], decor: new Array<string | null>(9).fill(null) };
}

/** Hình chữ nhật CẮT trong ảnh nguồn — đơn vị PIXEL ẢNH, không phải px màn hình. */
export interface CoverCrop {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * CẮT ẢNH THEO KIỂU "COVER" — MỘT tỉ lệ duy nhất cho cả 2 chiều ⇒ KHÔNG MÉO, KHÔNG LẶP.
 *
 * ⚠️ VÌ SAO CẦN HÀM THUẦN NÀY (thay vì mỗi chỗ tự tính):
 * cả `ArenaView` (10-wave) lẫn `ArenaBattleView` (Arena PVE) đều phải đặt MỘT ảnh bản đồ vào một
 * khung có tỉ lệ hơi khác ảnh. Nếu mỗi nơi tự `setDisplaySize(khung)` thì ảnh bị BÓP MÉO — đúng lỗi
 * đã có trong repo ("nền bị bẹp dọc 2,6 lần", xem chú thích `ArenaView.applyArt`). Nếu lặp (tile) thì
 * ảnh BẢN ĐỒ (có sông + 2 cầu) lặp sông/cầu khắp sân. Cách đúng: cắt một hình chữ nhật ĐÚNG TỈ LỆ
 * KHUNG ở giữa ảnh rồi mới đặt vào khung ⇒ tỉ lệ giữ nguyên 100%, ảnh chỉ xuất hiện MỘT lần.
 *
 * Hàm THUẦN (không tạo GameObject, không đụng Phaser) nên quy tắc "cắt ở giữa, không méo" khoá được
 * bằng số. Tham số rác (0/ âm/ NaN) ⇒ trả nguyên khung ảnh (không cắt), KHÔNG ném — đây là đường vẽ,
 * ném lỗi ở đây là làm trắng màn hình vì một ảnh hỏng.
 */
export function coverCrop(srcW: number, srcH: number, dstW: number, dstH: number): CoverCrop {
  const full: CoverCrop = { x: 0, y: 0, width: Math.max(1, Math.round(srcW)), height: Math.max(1, Math.round(srcH)) };
  if (!(srcW > 0) || !(srcH > 0) || !(dstW > 0) || !(dstH > 0)) return full;
  const srcAspect = srcW / srcH;
  const dstAspect = dstW / dstH;
  if (srcAspect > dstAspect) {
    // ảnh RỘNG hơn khung ⇒ giữ trọn chiều cao, cắt hai bên (căn giữa)
    const width = Math.max(1, Math.min(Math.round(srcW), Math.round(srcH * dstAspect)));
    return { x: Math.round((srcW - width) / 2), y: 0, width, height: Math.round(srcH) };
  }
  // ảnh CAO hơn khung ⇒ giữ trọn chiều ngang, cắt trên/ dưới (căn giữa)
  const height = Math.max(1, Math.min(Math.round(srcH), Math.round(srcW / dstAspect)));
  return { x: 0, y: Math.round((srcH - height) / 2), width: Math.round(srcW), height };
}

function loadImage(src: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    // 404 / file hỏng ⇒ trả null, KHÔNG reject: nơi gọi không phải try/catch từng ảnh
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

/**
 * Tách nền MAGENTA: pixel càng gần #FF00FF thì càng trong suốt.
 *
 * Vì sao có 2 ngưỡng (130/210): mép hình vẽ bị AI làm nhoè nên nằm lưng chừng giữa
 * màu prop và màu nền. Cắt cứng ở một ngưỡng sẽ để lại VIỀN HỒNG quanh prop. Nên
 * vùng lưng chừng được cho alpha giảm dần, rồi trừ luôn "spill" đỏ/hồng còn sót.
 */
function chromaKeyMagenta(data: Uint8ClampedArray): void {
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const d = Math.hypot(r - 255, g, b - 255); // khoảng cách tới magenta
    if (d < 130) {
      data[i + 3] = 0;
    } else if (d < 210) {
      data[i + 3] = Math.min(data[i + 3], Math.round(((d - 130) / 80) * 255));
      const spill = Math.min(r, b) - g;
      if (spill > 0) {
        data[i] = Math.max(0, r - spill);
        data[i + 2] = Math.max(0, b - spill);
      }
    }
  }
}

/**
 * GIỮ LẠI KHỐI LIỀN LỚN NHẤT trong 1 ô.
 *
 * Vì sao: prop/Nhà vẽ to thường TRÀN sang ô bên cạnh. Hộp bao của ô khi đó gồm cả
 * mảnh hàng xóm ⇒ ảnh bị kéo dài và lệch. Bộ lọc xoá mọi mảng "dính" từ ô khác.
 * Điểm ưu tiên = diện tích × (1 − độ lệch tâm): khối to nhưng lệch hẳn về một góc
 * (tức là mảnh tràn từ ô bên) sẽ thua khối chính giữa ô.
 */
function keepMainBlob(data: Uint8ClampedArray, w: number, h: number): void {
  const solid = (p: number): boolean => data[p * 4 + 3] > 16;
  const label = new Int32Array(w * h).fill(-1);
  const stack: number[] = [];
  let bestId = -1;
  let bestScore = -Infinity;
  let id = 0;

  for (let start = 0; start < w * h; start++) {
    if (label[start] !== -1 || !solid(start)) continue;
    const myId = id++;
    let area = 0;
    let minX = w;
    let minY = h;
    let maxX = -1;
    let maxY = -1;
    label[start] = myId;
    stack.push(start);
    while (stack.length) {
      const p = stack.pop() as number;
      const x = p % w;
      const y = (p - x) / w;
      area++;
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
      if (x > 0 && label[p - 1] === -1 && solid(p - 1)) (label[p - 1] = myId), stack.push(p - 1);
      if (x < w - 1 && label[p + 1] === -1 && solid(p + 1)) (label[p + 1] = myId), stack.push(p + 1);
      if (y > 0 && label[p - w] === -1 && solid(p - w)) (label[p - w] = myId), stack.push(p - w);
      if (y < h - 1 && label[p + w] === -1 && solid(p + w)) (label[p + w] = myId), stack.push(p + w);
    }
    const cx = (minX + maxX) / 2;
    const cy = (minY + maxY) / 2;
    const off = Math.hypot(cx - w / 2, cy - h / 2) / Math.max(w, h);
    const score = area * (1 - Math.min(0.92, off * 1.8));
    if (score > bestScore) {
      bestScore = score;
      bestId = myId;
    }
  }
  if (id <= 1) return; // chỉ 0 hoặc 1 khối ⇒ không có gì để loại
  for (let i = 0; i < w * h; i++) {
    if (label[i] !== -1 && label[i] !== bestId) data[i * 4 + 3] = 0;
  }
}

/** Cắt sát viền pixel đặc (pad 2px cho khỏi cụt mép). Trả null nếu ô TRỐNG. */
function trimCanvas(src: HTMLCanvasElement, pad = 2): HTMLCanvasElement | null {
  const ctx = src.getContext('2d', { willReadFrequently: true })!;
  const { width: w, height: h } = src;
  const d = ctx.getImageData(0, 0, w, h).data;
  let minX = w;
  let minY = h;
  let maxX = -1;
  let maxY = -1;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (d[(y * w + x) * 4 + 3] > 16) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (maxX < 0 || maxY < 0) return null; // ô trống (agy để trống đúng như prompt)
  minX = Math.max(0, minX - pad);
  minY = Math.max(0, minY - pad);
  maxX = Math.min(w - 1, maxX + pad);
  maxY = Math.min(h - 1, maxY + pad);
  const tw = maxX - minX + 1;
  const th = maxY - minY + 1;
  const out = document.createElement('canvas');
  out.width = tw;
  out.height = th;
  out.getContext('2d')!.drawImage(src, minX, minY, tw, th, 0, 0, tw, th);
  return out;
}

/** Ảnh thật (không chroma key) → canvas để nạp thẳng làm texture. */
function toCanvas(img: HTMLImageElement): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = img.width;
  c.height = img.height;
  c.getContext('2d')!.drawImage(img, 0, 0);
  return c;
}

/** Nạp 1 ảnh "ảnh thật" (sàn/nền) thành texture. Trả về khoá hoặc null nếu không được. */
function addPlain(scene: BattlefieldArtScene, key: string, img: HTMLImageElement): string | null {
  try {
    // Đã nạp ở lần vào sân trước (scene restart) ⇒ dùng lại, không tạo texture trùng
    if (!scene.textures.exists(key)) scene.textures.addCanvas(key, toCanvas(img));
    return scene.textures.exists(key) ? key : null;
  } catch (e) {
    console.warn(`[battlefield-art] không nạp được texture "${key}":`, e);
    return null;
  }
}

/**
 * Cắt ảnh lưới cols×rows thành từng ô riêng: tách magenta → lọc khối chính →
 * cắt sát viền → nạp texture. Trả về mảng khoá (null = ô trống / lỗi).
 */
function sliceSheet(
  scene: BattlefieldArtScene,
  img: HTMLImageElement,
  cols: number,
  rows: number,
  keyOf: (i: number) => string,
  label: string,
): Array<string | null> {
  const out: Array<string | null> = new Array(cols * rows).fill(null);
  const cw = Math.floor(img.width / cols);
  const ch = Math.floor(img.height / rows);
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const i = r * cols + c;
      const cell = document.createElement('canvas');
      cell.width = cw;
      cell.height = ch;
      const cctx = cell.getContext('2d', { willReadFrequently: true })!;
      cctx.drawImage(img, c * cw, r * ch, cw, ch, 0, 0, cw, ch);

      const id = cctx.getImageData(0, 0, cw, ch);
      chromaKeyMagenta(id.data);
      cctx.putImageData(id, 0, 0);
      keepMainBlob(id.data, cw, ch);
      cctx.putImageData(id, 0, 0);

      const trimmed = trimCanvas(cell);
      if (!trimmed) {
        // Ô trống là BÌNH THƯỜNG (prompt yêu cầu để trống) ⇒ chỉ log, không phải lỗi
        console.log(`[battlefield-art]   ${label} ô ${i + 1}: trống`);
        continue;
      }
      const key = keyOf(i);
      try {
        if (!scene.textures.exists(key)) scene.textures.addCanvas(key, trimmed);
        out[i] = scene.textures.exists(key) ? key : null;
      } catch (e) {
        console.warn(`[battlefield-art]   ${label} ô ${i + 1}: không nạp được texture:`, e);
        continue;
      }
      console.log(`[battlefield-art]   ${label} ô ${i + 1}: ${trimmed.width}×${trimmed.height} → ${key}`);
    }
  }
  return out;
}

/**
 * Nạp toàn bộ art sân đấu. Chỉ nạp 1 lần cho mỗi ảnh (texture dùng chung cả game).
 *
 * Gọi từ `ArenaView`:
 *   void loadBattlefieldArt(scene).then((art) => this.applyArt(art));
 * Nhờ vậy lúc mở màn KHÔNG chờ ảnh: sân hiện ngay bằng bản vẽ code, ảnh nạp xong
 * (kể cả sau vài trăm ms) thì thay thế tại chỗ.
 */
export async function loadBattlefieldArt(scene: BattlefieldArtScene): Promise<BattlefieldArt> {
  const art = emptyArt();
  try {
    // Nạp song song 5 ảnh — tuần tự sẽ làm chậm màn chơi không cần thiết
    // `artUrl()` đổi đuôi `.png` → đuôi THẬT (sync-art nén sang `.webp`, xem src/art/art-url.ts)
    const [bgImg, floorImg, riverImg, houseImg, decorImg] = await Promise.all([
      loadImage(artUrl(PATH.bg)),
      loadImage(artUrl(PATH.floor)),
      loadImage(artUrl(PATH.river)),
      loadImage(artUrl(PATH.house)),
      loadImage(artUrl(PATH.decor)),
    ]);

    if (bgImg) art.bg = addPlain(scene, KEY.bg, bgImg) ?? undefined;
    if (floorImg) art.floor = addPlain(scene, KEY.floor, floorImg) ?? undefined;
    if (riverImg) art.river = addPlain(scene, KEY.river, riverImg) ?? undefined;
    if (houseImg) art.house = sliceSheet(scene, houseImg, 3, 3, KEY.house, 'house').slice(0, 3).map((key) => (key ? { key } : null));
    if (decorImg) art.decor = sliceSheet(scene, decorImg, 3, 3, KEY.decor, 'arena-decor');

    const loaded = [art.bg && 'nền', art.floor && 'sàn', art.river && 'sân sông', art.house.some(Boolean) && 'Nhà', art.decor.some(Boolean) && 'decor']
      .filter(Boolean)
      .join(', ');
    if (loaded) console.log(`[battlefield-art] đã nạp: ${loaded}`);
    else console.log('[battlefield-art] chưa có ảnh AI nào — sân giữ bản vẽ code (chạy `npm run sync:art`?)');
  } catch (e) {
    // Bất kỳ lỗi bất ngờ nào (scene đã shutdown, canvas hết bộ nhớ…) ⇒ lùi về bản vẽ code.
    // Thà sân xấu còn hơn màn hình trắng. Trả về `art` (không phải emptyArt) để GIỮ
    // những phần đã nạp được trước khi lỗi — nền đúng mà Nhà sai vẫn hơn cả sân sai.
    console.warn('[battlefield-art] lỗi khi nạp ảnh AI — giữ bản vẽ code cho phần thiếu:', e);
  }
  return art;
}
